import React, { useEffect, useState, createElement } from 'react';
import { MessageSquare, ThumbsUp, Eye, Plus, Search, X, CheckCircle, Book, MessageCircle, Image as ImageIcon, Send, HelpCircle, Info, Upload, Trash2, CheckCheck, ArrowDownAZ, MessageSquareText, Heart, Filter, ChevronDown, Star, User, Users, MessageSquareDashed, ListFilter, Bookmark } from 'lucide-react';
import { ForumTopic } from './ForumTopic';
import { NewForumTopic } from './NewForumTopic';
import { IconTooltip } from './IconTooltip';
import { IconFilter } from './IconFilter';
import { useToast } from './Toast';
// LGS subject categories
const lgsSubjects = [{
  id: 'turkce',
  title: 'Türkçe',
  topics: ['Sözcükte Anlam', 'Deyimler ve Atasözleri', 'Cümlede Anlam', 'Sözel Mantık / Görsel Okuma', 'Parçada Anlam', 'Söz Sanatları', 'Metin Türleri', 'Noktalama İşaretleri', 'Yazım Kuralları', 'Fiilimsiler', 'Cümlenin Öğeleri', 'Cümle Türleri', 'Anlatım Bozukluğu']
}, {
  id: 'matematik',
  title: 'Matematik',
  topics: ['Çarpanlar ve Katlar', 'Üslü Sayılar', 'Köklü Sayılar', 'Veri Analizi', 'Olasılık', 'Cebirsel İfadeler', 'Doğrusal Denklemler ve Eğim', 'Eşitsizlikler', 'Üçgenler', 'Eşlik ve Benzerlik', 'Dönüşüm Geometrisi', 'Geometrik Cisimler']
}, {
  id: 'fen',
  title: 'Fen Bilimleri',
  topics: ['Mevsimler ve İklim', 'DNA ve Genetik Kod', 'Basınç', 'Madde ve Endüstri', 'Basit Makineler', 'Enerji Dönüşümleri', 'Elektrik Yükleri']
}, {
  id: 'inkilap',
  title: 'T.C. İnkılap Tarihi',
  topics: ['Bir Kahraman Doğuyor', 'Milli Uyanış', 'Milli Bir Destan', 'Atatürkçülük', 'Demokratikleşme Çabaları', 'Atatürk Dönemi Dış Politika']
}, {
  id: 'ingilizce',
  title: 'İngilizce',
  topics: ['Friendship', 'Teen Life', 'In The Kitchen', 'On The Phone', 'The Internet', 'Adventures', 'Tourism', 'Chores', 'Science', 'Natural Forces']
}, {
  id: 'din',
  title: 'Din Kültürü',
  topics: ['Kader İnancı', 'Hz. Musa', 'Zekat ve Sadaka', 'Din ve Hayat', 'Hz. Muhammed', "Kur'an-ı Kerim", 'Paylaşma ve Yardımlaşma']
}];
// Topic interface
interface ForumTopicType {
  id: number;
  title: string;
  author: string;
  authorType: 'student' | 'teacher';
  category: string;
  topic?: string;
  replies: number;
  views: number;
  likes: number;
  time: string;
  content?: string;
  visibility: 'public' | 'private' | 'institution';
  selectedUsers?: string[];
  type: 'question' | 'other';
  bestAnswerId?: number;
  teacherApprovedId?: number;
  solved?: boolean;
  image?: string;
  likedBy?: string[];
  mostLikedAnswerId?: number;
  repliedBy?: string[];
  archived?: boolean;
}
// Get category color
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Matematik':
      return 'bg-blue-400/10 text-blue-400';
    case 'Türkçe':
      return 'bg-red-400/10 text-red-400';
    case 'Fen Bilimleri':
      return 'bg-green-400/10 text-green-400';
    case 'T.C. İnkılap Tarihi':
      return 'bg-amber-400/10 text-amber-400';
    case 'İngilizce':
      return 'bg-indigo-400/10 text-indigo-400';
    case 'Din Kültürü':
      return 'bg-teal-400/10 text-teal-400';
    default:
      return 'bg-emerald-400/10 text-emerald-400';
  }
};
const Forum = () => {
  // Mock toast function if useToast hook is not available
  const {
    toast = (message: string) => console.log(message)
  } = useToast ? useToast() : {};
  // State for forum topics
  const [topics, setTopics] = useState<ForumTopicType[]>(() => {
    const savedTopics = localStorage.getItem('teacherForumTopics');
    if (savedTopics) {
      return JSON.parse(savedTopics);
    }
    // Default topics if none in localStorage
    return [{
      id: 1,
      title: 'Üslü sayılarda negatif kuvvetlerin anlaşılması için etkili yöntemler',
      author: 'Öğretmen',
      authorType: 'teacher',
      category: 'Matematik',
      topic: 'Üslü Sayılar',
      replies: 5,
      views: 42,
      likes: 12,
      time: '2 gün önce',
      content: 'Öğrencilerim üslü sayılarda özellikle negatif kuvvetlerin işlemlerinde zorlanıyorlar. Bunu daha iyi anlayabilmeleri için kullandığınız etkili yöntemler var mı?',
      visibility: 'public',
      type: 'question',
      likedBy: [],
      repliedBy: ['Zeynep Öğretmen', 'Mehmet K.']
    }, {
      id: 2,
      title: 'Dil bilgisi konularında materyal önerileri',
      author: 'Ahmet Y.',
      authorType: 'teacher',
      category: 'Türkçe',
      topic: 'Fiilimsiler',
      replies: 3,
      views: 28,
      likes: 7,
      time: '1 hafta önce',
      content: 'Fiilimsiler konusunu anlatırken kullanabileceğim etkileşimli materyal önerileri olan var mı?',
      visibility: 'institution',
      type: 'question',
      likedBy: ['Öğretmen'],
      repliedBy: []
    }, {
      id: 3,
      title: 'Sınıf yönetiminde yeni yaklaşımlar',
      author: 'Zeynep K.',
      authorType: 'teacher',
      category: 'Genel',
      replies: 8,
      views: 56,
      likes: 15,
      time: '2 hafta önce',
      content: 'Son zamanlarda uyguladığım bazı sınıf yönetimi tekniklerini paylaşmak istiyorum. Özellikle dikkat dağınıklığı olan öğrencilerde etkili oluyor.',
      visibility: 'public',
      type: 'other',
      likedBy: [],
      repliedBy: ['Öğretmen']
    }];
  });
  // Save topics to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('teacherForumTopics', JSON.stringify(topics));
  }, [topics]);
  // State for UI controls
  const [selectedTopic, setSelectedTopic] = useState<ForumTopicType | null>(null);
  const [activeTab, setActiveTab] = useState<'question' | 'other'>('question');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState<string>('');
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<string>('');
  const [personalFilter, setPersonalFilter] = useState<'all' | 'my-topics' | 'my-replies'>('all');
  const [activeFilters, setActiveFilters] = useState<{
    teacherApproved: boolean;
    studentSelected: boolean;
    mostLiked: boolean;
  }>({
    teacherApproved: false,
    studentSelected: false,
    mostLiked: false
  });
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'institution'>('all');
  const [sortOption, setSortOption] = useState<'newest' | 'mostReplies' | 'mostLikes'>('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);
  // Filter topics based on current filters
  const filteredTopics = topics.filter(topic => {
    // Filter by tab (question or other)
    if (topic.type !== activeTab) return false;
    // Filter by search term
    if (searchTerm && !topic.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    // Filter by subject
    if (selectedSubjectFilter && topic.category !== selectedSubjectFilter) return false;
    // Filter by topic
    if (selectedTopicFilter && topic.topic !== selectedTopicFilter) return false;
    // Filter by personal (my topics, my replies)
    if (personalFilter === 'my-topics' && topic.author !== 'Öğretmen') return false;
    if (personalFilter === 'my-replies' && !topic.repliedBy?.includes('Öğretmen')) return false;
    // Filter by visibility
    if (visibilityFilter !== 'all' && topic.visibility !== visibilityFilter) return false;
    // Filter by archived status
    if (topic.archived) return false;
    // Additional filters (teacher approved, student selected, most liked)
    if (activeFilters.teacherApproved && !topic.teacherApprovedId) return false;
    if (activeFilters.studentSelected && !topic.bestAnswerId) return false;
    if (activeFilters.mostLiked && !topic.mostLikedAnswerId) return false;
    return true;
  });
  // Sort filtered topics
  const sortTopics = (topics: ForumTopicType[]) => {
    switch (sortOption) {
      case 'newest':
        return [...topics].sort((a, b) => a.time < b.time ? 1 : -1);
      case 'mostReplies':
        return [...topics].sort((a, b) => b.replies - a.replies);
      case 'mostLikes':
        return [...topics].sort((a, b) => b.likes - a.likes);
      default:
        return topics;
    }
  };
  const sortedTopics = sortTopics(filteredTopics);
  // Handle topic selection
  const handleSelectTopic = (topic: ForumTopicType) => {
    // Update view count
    setTopics(topics.map(t => {
      if (t.id === topic.id) {
        return {
          ...t,
          views: t.views + 1
        };
      }
      return t;
    }));
    setSelectedTopic(topic);
  };
  // Handle new topic submission
  const handleSubmitNewTopic = (topicData: any) => {
    const newTopic: ForumTopicType = {
      id: Date.now(),
      title: topicData.title,
      author: 'Öğretmen',
      authorType: 'teacher',
      category: topicData.category || '',
      topic: topicData.topic || '',
      replies: 0,
      views: 0,
      likes: 0,
      likedBy: [],
      time: 'Şimdi',
      content: topicData.content,
      visibility: topicData.visibility || 'public',
      selectedUsers: topicData.selectedUsers || [],
      type: activeTab,
      image: topicData.image ? URL.createObjectURL(topicData.image) : undefined,
      repliedBy: []
    };
    setTopics([newTopic, ...topics]);
    setShowNewTopic(false);
    toast('Konu başarıyla oluşturuldu!');
    setSelectedTopic(newTopic);
  };
  // Handle reply addition
  const handleAddReply = (topicId: number, replyContent: string) => {
    setTopics(topics.map(topic => {
      if (topic.id === topicId) {
        return {
          ...topic,
          replies: topic.replies + 1,
          repliedBy: [...(topic.repliedBy || []), 'Öğretmen']
        };
      }
      return topic;
    }));
    if (selectedTopic && selectedTopic.id === topicId) {
      setSelectedTopic({
        ...selectedTopic,
        replies: selectedTopic.replies + 1,
        repliedBy: [...(selectedTopic.repliedBy || []), 'Öğretmen']
      });
    }
    toast('Yanıtınız gönderildi!');
  };
  // Handle topic like
  const handleLikeTopic = (topicId: number) => {
    setTopics(topics.map(topic => {
      if (topic.id === topicId) {
        const isLiked = topic.likedBy?.includes('Öğretmen') || false;
        return {
          ...topic,
          likes: isLiked ? topic.likes - 1 : topic.likes + 1,
          likedBy: isLiked ? (topic.likedBy || []).filter(user => user !== 'Öğretmen') : [...(topic.likedBy || []), 'Öğretmen']
        };
      }
      return topic;
    }));
    if (selectedTopic && selectedTopic.id === topicId) {
      const isLiked = selectedTopic.likedBy?.includes('Öğretmen') || false;
      setSelectedTopic({
        ...selectedTopic,
        likes: isLiked ? selectedTopic.likes - 1 : selectedTopic.likes + 1,
        likedBy: isLiked ? (selectedTopic.likedBy || []).filter(user => user !== 'Öğretmen') : [...(selectedTopic.likedBy || []), 'Öğretmen']
      });
    }
  };
  // Handle topic deletion (teacher specific functionality)
  const handleDeleteTopic = (topicId: number) => {
    setTopics(topics.filter(topic => topic.id !== topicId));
    toast('Konu başarıyla silindi!');
    if (selectedTopic && selectedTopic.id === topicId) {
      setSelectedTopic(null);
    }
  };
  // Handle best answer update
  const handleUpdateBestAnswer = (topicId: number, answerId: number) => {
    setTopics(topics.map(topic => {
      if (topic.id === topicId) {
        return {
          ...topic,
          bestAnswerId: topic.bestAnswerId === answerId ? undefined : answerId,
          solved: topic.bestAnswerId !== answerId
        };
      }
      return topic;
    }));
    if (selectedTopic && selectedTopic.id === topicId) {
      setSelectedTopic({
        ...selectedTopic,
        bestAnswerId: selectedTopic.bestAnswerId === answerId ? undefined : answerId,
        solved: selectedTopic.bestAnswerId !== answerId
      });
    }
    toast('En iyi cevap güncellendi!');
  };
  // Handle teacher approved answer update
  const handleUpdateTeacherApproved = (topicId: number, answerId: number) => {
    setTopics(topics.map(topic => {
      if (topic.id === topicId) {
        return {
          ...topic,
          teacherApprovedId: topic.teacherApprovedId === answerId ? undefined : answerId
        };
      }
      return topic;
    }));
    if (selectedTopic && selectedTopic.id === topicId) {
      setSelectedTopic({
        ...selectedTopic,
        teacherApprovedId: selectedTopic.teacherApprovedId === answerId ? undefined : answerId
      });
    }
    toast('Öğretmen onaylı cevap güncellendi!');
  };
  // Handle archive topic
  const handleArchiveTopic = (topicId: number) => {
    setTopics(topics.map(topic => {
      if (topic.id === topicId) {
        return {
          ...topic,
          archived: !topic.archived
        };
      }
      return topic;
    }));
    toast('Konu arşivlendi!');
    if (selectedTopic && selectedTopic.id === topicId) {
      setSelectedTopic(null);
    }
  };
  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedSubjectFilter('');
    setSelectedTopicFilter('');
    setPersonalFilter('all');
    setActiveFilters({
      teacherApproved: false,
      studentSelected: false,
      mostLiked: false
    });
    setVisibilityFilter('all');
    setSortOption('newest');
  };
  // Get available topics based on selected subject
  const getAvailableTopics = () => {
    if (!selectedSubjectFilter) return [];
    const subject = lgsSubjects.find(s => s.title === selectedSubjectFilter);
    return subject ? subject.topics : [];
  };
  return <div className="min-h-screen bg-black text-white" data-id="element-515">
      {selectedTopic ? <ForumTopic topic={selectedTopic} onClose={() => setSelectedTopic(null)} onAddReply={handleAddReply} onUpdateBestAnswer={handleUpdateBestAnswer} onUpdateTeacherApproved={handleUpdateTeacherApproved} onArchive={handleArchiveTopic} data-id="element-516" /> : <div className="p-4 space-y-6" data-id="element-517">
          {/* Header */}
          <div className="flex justify-between items-center mb-6" data-id="element-518">
            <h1 className="text-2xl font-bold text-white" data-id="element-519">Forum</h1>
            <div className="flex items-center gap-3" data-id="element-520">
              {/* Arama kutusu - yeşil gradyan border */}
              <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-521">
                <div className="relative bg-black/90 rounded-lg" data-id="element-522">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" data-id="element-523" />
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Ara..." className="w-full bg-transparent text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none" data-id="element-524" />
                </div>
              </div>
              {/* Soru Sor butonu - yeşil gradyan */}
              <button onClick={() => setShowNewTopic(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg text-white hover:from-emerald-700 hover:to-teal-700 transition-colors" data-id="element-525">
                <Plus className="w-4 h-4" data-id="element-526" />
                Soru Sor
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex border-b border-gray-800 mb-6" data-id="element-527">
            <button onClick={() => setActiveTab('question')} className={`relative py-3 px-6 font-medium text-sm transition-colors ${activeTab === 'question' ? 'text-emerald-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-528">
              <div className="flex items-center" data-id="element-529">
                <HelpCircle className="w-4 h-4 mr-2" data-id="element-530" />
                Soru Çözüm
              </div>
              {activeTab === 'question' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-500" data-id="element-531"></div>}
            </button>
            <button onClick={() => setActiveTab('other')} className={`relative py-3 px-6 font-medium text-sm transition-colors ${activeTab === 'other' ? 'text-emerald-400' : 'text-gray-400 hover:text-gray-300'}`} data-id="element-532">
              <div className="flex items-center" data-id="element-533">
                <MessageCircle className="w-4 h-4 mr-2" data-id="element-534" />
                Diğer
              </div>
              {activeTab === 'other' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-500" data-id="element-535"></div>}
            </button>
          </div>
          {/* Personal Filter Buttons */}
          <div className="flex flex-wrap gap-3 mb-4" data-id="element-536">
            <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-537">
              <div className="relative bg-black/95 rounded-lg" data-id="element-538">
                <div className="flex flex-wrap gap-4 items-center text-sm" data-id="element-539">
                  <span className="text-gray-300 font-medium" data-id="element-540">Filtrele:</span>
                  {/* Öğretmen Onaylı Çözüm - YEŞİL */}
                  <button onClick={() => setActiveFilters({
                ...activeFilters,
                teacherApproved: !activeFilters.teacherApproved
              })} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${activeFilters.teacherApproved ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-white/5 text-gray-400 hover:bg-green-500/10 hover:text-green-400'}`} data-id="element-541">
                    <CheckCheck className="w-4 h-4" data-id="element-542" />
                    <span data-id="element-543">Öğretmen Onaylı Çözüm</span>
                  </button>
                  {/* En Beğenilen Yanıt - MOR */}
                  <button onClick={() => setActiveFilters({
                ...activeFilters,
                mostLiked: !activeFilters.mostLiked
              })} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${activeFilters.mostLiked ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-white/5 text-gray-400 hover:bg-purple-500/10 hover:text-purple-400'}`} data-id="element-544">
                    <Heart className="w-4 h-4" data-id="element-545" />
                    <span data-id="element-546">En Beğenilen Yanıt</span>
                  </button>
                  {/* Aradığım Cevap - MAVİ */}
                  <button onClick={() => setActiveFilters({
                ...activeFilters,
                studentSelected: !activeFilters.studentSelected
              })} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${activeFilters.studentSelected ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : 'bg-white/5 text-gray-400 hover:bg-blue-500/10 hover:text-blue-400'}`} data-id="element-547">
                    <Star className="w-4 h-4" data-id="element-548" />
                    <span data-id="element-549">Aradığım Cevap</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Dropdown Filters */}
          <div className="flex flex-wrap gap-3 mb-4" data-id="element-550">
            {/* Ders Filter - YEŞİL DROPDOWN */}
            {activeTab === 'question' && <div className="relative" data-id="element-551">
                <button onClick={() => setShowSubjectDropdown(!showSubjectDropdown)} className="flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-lg border border-emerald-500/30 hover:border-emerald-500/50 transition-colors" data-id="element-552">
                  <Filter className="w-4 h-4" data-id="element-553" />
                  <span data-id="element-554">Ders: </span>
                  <span className="text-emerald-400" data-id="element-555">
                    {selectedSubjectFilter || 'Tümü'}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-1" data-id="element-556" />
                </button>
                {showSubjectDropdown && <div className="absolute left-0 z-10 mt-1 w-56 bg-black/90 rounded-lg shadow-lg border border-emerald-500/30 max-h-60 overflow-y-auto" data-id="element-557">
                    <button onClick={() => {
              setSelectedSubjectFilter('');
              setShowSubjectDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${selectedSubjectFilter === '' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-558">
                      Tümü
                    </button>
                    {lgsSubjects.map(subject => <button key={subject.id} onClick={() => {
              setSelectedSubjectFilter(subject.title);
              setShowSubjectDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${selectedSubjectFilter === subject.title ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-559">
                        {subject.title}
                      </button>)}
                  </div>}
              </div>}
            {/* Konu Filter - YEŞİL DROPDOWN */}
            {activeTab === 'question' && <div className="relative" data-id="element-560">
                <button onClick={() => {
            if (selectedSubjectFilter) {
              setShowTopicDropdown(!showTopicDropdown);
            }
          }} disabled={!selectedSubjectFilter} className={`flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-lg border border-emerald-500/30 transition-colors ${selectedSubjectFilter ? 'hover:border-emerald-500/50' : 'opacity-50 cursor-not-allowed'}`} data-id="element-561">
                  <Filter className="w-4 h-4" data-id="element-562" />
                  <span data-id="element-563">Konu: </span>
                  <span className="text-emerald-400" data-id="element-564">
                    {selectedTopicFilter || 'Tümü'}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-1" data-id="element-565" />
                </button>
                {showTopicDropdown && selectedSubjectFilter && <div className="absolute left-0 z-10 mt-1 w-56 bg-black/90 rounded-lg shadow-lg border border-emerald-500/30 max-h-60 overflow-y-auto" data-id="element-566">
                    <button onClick={() => {
              setSelectedTopicFilter('');
              setShowTopicDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${selectedTopicFilter === '' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-567">
                      Tümü
                    </button>
                    {getAvailableTopics().map(topic => <button key={topic} onClick={() => {
              setSelectedTopicFilter(topic);
              setShowTopicDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${selectedTopicFilter === topic ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-568">
                        {topic}
                      </button>)}
                  </div>}
              </div>}
            {/* Görünürlük Filter - YEŞİL DROPDOWN */}
            <div className="relative" data-id="element-569">
              <button onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)} className="flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-lg border border-emerald-500/30 hover:border-emerald-500/50 transition-colors" data-id="element-570">
                <Eye className="w-4 h-4" data-id="element-571" />
                <span data-id="element-572">Görünürlük: </span>
                <span className="text-emerald-400" data-id="element-573">
                  {visibilityFilter === 'all' ? 'Tümü' : visibilityFilter === 'public' ? 'Herkese Açık' : 'Kurum İçi'}
                </span>
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-574" />
              </button>
              {showVisibilityDropdown && <div className="absolute left-0 z-10 mt-1 w-56 bg-black/90 rounded-lg shadow-lg border border-emerald-500/30" data-id="element-575">
                  <button onClick={() => {
              setVisibilityFilter('all');
              setShowVisibilityDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${visibilityFilter === 'all' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-576">
                    Tümü
                  </button>
                  <button onClick={() => {
              setVisibilityFilter('public');
              setShowVisibilityDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${visibilityFilter === 'public' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-577">
                    Herkese Açık
                  </button>
                  <button onClick={() => {
              setVisibilityFilter('institution');
              setShowVisibilityDropdown(false);
            }} className={`w-full px-4 py-2 text-left hover:bg-emerald-500/20 ${visibilityFilter === 'institution' ? 'bg-emerald-500/20 text-emerald-400' : 'text-white'}`} data-id="element-578">
                    Kurum İçi
                  </button>
                </div>}
            </div>
            {/* Sort dropdown */}
            <div className="relative" data-id="element-579">
              <button onClick={() => setShowSortDropdown(!showSortDropdown)} className="flex items-center gap-2 px-4 py-2 bg-black/50 text-white rounded-lg border border-emerald-500/30 hover:border-emerald-500/50 transition-colors" data-id="element-580">
                <ArrowDownAZ className="w-4 h-4" data-id="element-581" />
                <span data-id="element-582">Sıralama: </span>
                <span className="text-emerald-400" data-id="element-583">
                  {sortOption === 'newest' ? 'En Yeni' : sortOption === 'mostReplies' ? 'En Çok Yanıt' : 'En Çok Beğeni'}
                </span>
                <ChevronDown className="w-4 h-4 ml-1" data-id="element-584" />
              </button>
              {showSortDropdown && <div className="absolute left-0 mt-1 w-48 rounded-lg shadow-lg bg-black/90 border border-emerald-500/30 z-10" data-id="element-585">
                  <div className="p-1" data-id="element-586">
                    <button onClick={() => {
                setSortOption('newest');
                setShowSortDropdown(false);
              }} className={`w-full text-left px-4 py-2 rounded-lg ${sortOption === 'newest' ? 'bg-emerald-500/20 text-emerald-400' : 'hover:bg-emerald-500/10 text-white'}`} data-id="element-587">
                      En Yeni
                    </button>
                    <button onClick={() => {
                setSortOption('mostReplies');
                setShowSortDropdown(false);
              }} className={`w-full text-left px-4 py-2 rounded-lg ${sortOption === 'mostReplies' ? 'bg-emerald-500/20 text-emerald-400' : 'hover:bg-emerald-500/10 text-white'}`} data-id="element-588">
                      En Çok Yanıt
                    </button>
                    <button onClick={() => {
                setSortOption('mostLikes');
                setShowSortDropdown(false);
              }} className={`w-full text-left px-4 py-2 rounded-lg ${sortOption === 'mostLikes' ? 'bg-emerald-500/20 text-emerald-400' : 'hover:bg-emerald-500/10 text-white'}`} data-id="element-589">
                      En Çok Beğeni
                    </button>
                  </div>
                </div>}
            </div>
            {/* Reset filters */}
            {(searchTerm || selectedSubjectFilter || selectedTopicFilter || personalFilter !== 'all' || visibilityFilter !== 'all' || activeFilters.teacherApproved || activeFilters.studentSelected || activeFilters.mostLiked) && <button onClick={resetFilters} className="flex items-center gap-2 px-4 py-2 bg-black/50 text-gray-400 hover:text-gray-300 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors" data-id="element-590">
                <X className="w-4 h-4" data-id="element-591" />
                <span data-id="element-592">Filtreleri Temizle</span>
              </button>}
          </div>
          {/* Topic list */}
          <div className="space-y-4" data-id="element-593">
            {sortedTopics.length > 0 ? sortedTopics.map(topic => <div key={topic.id} onClick={() => handleSelectTopic(topic)} className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-emerald-600/30 to-teal-600/30 hover:from-emerald-600/50 hover:to-teal-600/50 transition-all duration-300 cursor-pointer" data-id="element-594">
                  <div className="bg-black rounded-lg p-4" data-id="element-595">
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-2" data-id="element-596">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-597">
                        {/* Category */}
                        {topic.category && <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(topic.category)}`} data-id="element-598">
                            {topic.category}
                          </span>}
                        {/* Topic */}
                        {topic.topic && <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-300" data-id="element-599">
                            {topic.topic}
                          </span>}
                        {/* Visibility */}
                        {topic.visibility === 'private' && <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 flex items-center" data-id="element-600">
                            <User className="w-3 h-3 mr-1" data-id="element-601" />
                            Özel
                          </span>}
                        {topic.visibility === 'institution' && <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 flex items-center" data-id="element-602">
                            <Users className="w-3 h-3 mr-1" data-id="element-603" />
                            Kurum İçi
                          </span>}
                        {/* Teacher approved */}
                        {topic.teacherApprovedId && <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 flex items-center" data-id="element-604">
                            <CheckCheck className="w-3 h-3 mr-1" data-id="element-605" />
                            Öğretmen Onaylı
                          </span>}
                        {/* Student selected */}
                        {topic.bestAnswerId && <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 flex items-center" data-id="element-606">
                            <Star className="w-3 h-3 mr-1" data-id="element-607" />
                            Aradığım Cevap
                          </span>}
                        {/* Most liked */}
                        {topic.mostLikedAnswerId && <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 flex items-center" data-id="element-608">
                            <Heart className="w-3 h-3 mr-1" data-id="element-609" />
                            En Beğenilen
                          </span>}
                      </div>
                      <div className="flex items-center text-gray-400 text-xs" data-id="element-610">
                        <span data-id="element-611">{topic.time}</span>
                        {/* TEACHER SPECIFIC: Delete button */}
                        <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteTopic(topic.id);
                }} className="ml-2 p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Konuyu Sil" data-id="element-612">
                          <Trash2 className="w-4 h-4" data-id="element-613" />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-lg font-medium mb-2" data-id="element-614">{topic.title}</h3>
                    <div className="flex flex-wrap items-center justify-between" data-id="element-615">
                      <div className="flex items-center text-sm text-gray-400" data-id="element-616">
                        <User className="w-4 h-4 mr-1" data-id="element-617" />
                        <span className={topic.authorType === 'teacher' ? 'text-emerald-400' : ''} data-id="element-618">
                          {topic.author}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-400" data-id="element-619">
                        <div className="flex items-center" data-id="element-620">
                          <MessageSquare className="w-4 h-4 mr-1" data-id="element-621" />
                          <span data-id="element-622">{topic.replies}</span>
                        </div>
                        <div className="flex items-center" data-id="element-623">
                          <Eye className="w-4 h-4 mr-1" data-id="element-624" />
                          <span data-id="element-625">{topic.views}</span>
                        </div>
                        <div className="flex items-center" data-id="element-626">
                          <button onClick={e => {
                    e.stopPropagation();
                    handleLikeTopic(topic.id);
                  }} className={`flex items-center ${topic.likedBy?.includes('Öğretmen') ? 'text-emerald-400' : 'hover:text-emerald-400'}`} data-id="element-627">
                            <ThumbsUp className="w-4 h-4 mr-1" data-id="element-628" />
                            <span data-id="element-629">{topic.likes}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>) : <div className="text-center py-12" data-id="element-630">
                <div className="flex justify-center mb-4" data-id="element-631">
                  {activeTab === 'question' ? <Book className="w-12 h-12 text-gray-600" data-id="element-632" /> : <MessageSquareDashed className="w-12 h-12 text-gray-600" data-id="element-633" />}
                </div>
                <h3 className="text-xl font-medium text-gray-400 mb-2" data-id="element-634">
                  {searchTerm || selectedSubjectFilter || selectedTopicFilter || personalFilter !== 'all' || visibilityFilter !== 'all' || activeFilters.teacherApproved || activeFilters.studentSelected || activeFilters.mostLiked ? 'Arama kriterlerinize uygun sonuç bulunamadı' : `Henüz ${activeTab === 'question' ? 'soru' : 'konu'} bulunmuyor`}
                </h3>
                <p className="text-gray-500 mb-4" data-id="element-635">
                  {searchTerm || selectedSubjectFilter || selectedTopicFilter || personalFilter !== 'all' || visibilityFilter !== 'all' || activeFilters.teacherApproved || activeFilters.studentSelected || activeFilters.mostLiked ? 'Lütfen farklı filtreler deneyiniz' : `İlk ${activeTab === 'question' ? 'soruyu' : 'konuyu'} siz oluşturun`}
                </p>
                {searchTerm || selectedSubjectFilter || selectedTopicFilter || personalFilter !== 'all' || visibilityFilter !== 'all' || activeFilters.teacherApproved || activeFilters.studentSelected || activeFilters.mostLiked ? <button onClick={resetFilters} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-636">
                    Filtreleri Temizle
                  </button> : <button onClick={() => setShowNewTopic(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2 rounded-lg transition-colors" data-id="element-637">
                    {activeTab === 'question' ? 'Yeni Soru Sor' : 'Yeni Konu Oluştur'}
                  </button>}
              </div>}
          </div>
        </div>}
      {/* New Topic Modal */}
      {showNewTopic && <NewForumTopic onClose={() => setShowNewTopic(false)} onSubmit={handleSubmitNewTopic} activeTab={activeTab} data-id="element-638" />}
    </div>;
};
export default Forum;